import { type EarlyAccessFeature, isEarlyAccessFeature } from "@rosc/contracts";
import { AppConfig } from "@/common/appConfig";
import { useAuthStore } from "@/store/auth.store";

// Early access: a feature is on when its build flag is on (staging, and
// production once it ships to everyone) or when the signed-in account has
// been granted it (GET /api/v2/me earlyAccess; granted only with the API's
// internal early-access script). On only through a grant, it is a Beta: the
// feature shows BetaBadge and "Send feedback on this beta".

// The build flag for each key, read when asked so tests can change
// AppConfig.
const BUILD_FLAGS: Record<EarlyAccessFeature, () => boolean> = {
  "guided-first-visit": () => AppConfig.enableGuidedFirstVisit,
  // Phase 1: on only for accounts granted early access.
  "radiology-dictation": () => false,
};

export const isBuildFlagOn = (feature: EarlyAccessFeature): boolean =>
  BUILD_FLAGS[feature]();

export interface FeatureState {
  /** Shown to this account. */
  on: boolean;
  /** Shown only through early access. */
  beta: boolean;
}

export const featureState = (
  feature: EarlyAccessFeature,
  earlyAccess: readonly string[] | null | undefined,
): FeatureState => {
  if (isBuildFlagOn(feature)) return { on: true, beta: false };
  const granted = (earlyAccess ?? []).some(
    (key) => isEarlyAccessFeature(key) && key === feature,
  );
  return { on: granted, beta: granted };
};

const grantedNow = (): readonly string[] | undefined =>
  useAuthStore.getState().userData?.earlyAccess;

/** Outside React (stores, event handlers): the signed-in account now. */
export const isFeatureOn = (feature: EarlyAccessFeature): boolean =>
  featureState(feature, grantedNow()).on;

/** Outside React: on only through early access. */
export const isBeta = (feature: EarlyAccessFeature): boolean =>
  featureState(feature, grantedNow()).beta;

const useGranted = (): readonly string[] | undefined =>
  useAuthStore((state) => state.userData?.earlyAccess);

/** Whether the feature is shown to the signed-in account. */
export const useFeature = (feature: EarlyAccessFeature): boolean =>
  featureState(feature, useGranted()).on;

/** Whether the feature is shown only through early access (a Beta). */
export const useIsBeta = (feature: EarlyAccessFeature): boolean =>
  featureState(feature, useGranted()).beta;
